import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Layout } from './components/Layout.js';
import { Loading } from './components/common.js';
import { useAuth } from './contexts/AuthContext.js';


/*
 * ページは route 単位で分割する。
 *
 * ここで静的に import すると、どのページを開いても全ページぶんの依存が
 * 初期バンドルに載る。問題ページが抱える Monaco (3.3 MB) と
 * KaTeX / Markdown (約 470 KB) がログイン画面でも毎回落ちてくることになる。
 */
const AdminContestEditPage = lazy(() => import('./pages/AdminContestPages.js').then((m) => ({ default: m.AdminContestEditPage })));
const AdminContestListPage = lazy(() => import('./pages/AdminContestPages.js').then((m) => ({ default: m.AdminContestListPage })));
const AdminGenerationPage = lazy(() => import('./pages/AdminGenerationPage.js').then((m) => ({ default: m.AdminGenerationPage })));
const AdminDashboardPage = lazy(() => import('./pages/AdminPages.js').then((m) => ({ default: m.AdminDashboardPage })));
const AdminProblemEditPage = lazy(() => import('./pages/AdminPages.js').then((m) => ({ default: m.AdminProblemEditPage })));
const AdminProblemListPage = lazy(() => import('./pages/AdminPages.js').then((m) => ({ default: m.AdminProblemListPage })));
const AdminTestcasePage = lazy(() => import('./pages/AdminPages.js').then((m) => ({ default: m.AdminTestcasePage })));
const LoginPage = lazy(() => import('./pages/AuthPages.js').then((m) => ({ default: m.LoginPage })));
const ResetPasswordPage = lazy(() => import('./pages/AuthPages.js').then((m) => ({ default: m.ResetPasswordPage })));
const SignupPage = lazy(() => import('./pages/AuthPages.js').then((m) => ({ default: m.SignupPage })));
const ContestListPage = lazy(() => import('./pages/ContestPages.js').then((m) => ({ default: m.ContestListPage })));
const ContestPage = lazy(() => import('./pages/ContestPages.js').then((m) => ({ default: m.ContestPage })));
const HelpPage = lazy(() => import('./pages/HelpPage.js').then((m) => ({ default: m.HelpPage })));
const HomePage = lazy(() => import('./pages/HomePage.js').then((m) => ({ default: m.HomePage })));
const NotFoundPage = lazy(() => import('./pages/HomePage.js').then((m) => ({ default: m.NotFoundPage })));
const ProblemListPage = lazy(() => import('./pages/ProblemListPage.js').then((m) => ({ default: m.ProblemListPage })));
const ProblemPage = lazy(() => import('./pages/ProblemPage.js').then((m) => ({ default: m.ProblemPage })));
const SubmissionDetailPage = lazy(() => import('./pages/SubmissionPages.js').then((m) => ({ default: m.SubmissionDetailPage })));
const SubmissionListPage = lazy(() => import('./pages/SubmissionPages.js').then((m) => ({ default: m.SubmissionListPage })));
const ProfilePage = lazy(() => import('./pages/UserPages.js').then((m) => ({ default: m.ProfilePage })));
const RankingPage = lazy(() => import('./pages/UserPages.js').then((m) => ({ default: m.RankingPage })));
const SettingsPage = lazy(() => import('./pages/UserPages.js').then((m) => ({ default: m.SettingsPage })));

/**
 * ログインが必要なページ。
 *
 * 画面を隠すだけで、認可そのものはサーバーが行う (SPEC 2.1-5)。
 * ここを迂回しても API が 401/403 を返すので、データは守られる。
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Loading />;
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

/** MODERATOR 以上向けのページ。こちらも表示制御にすぎない。 */
function RequireStaff({ children }: { children: ReactNode }) {
  const { session, isStaff, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Loading />;
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!isStaff) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
      <Route element={<Layout />}>
        <Route index element={<HomePage />} />

        <Route path="problems" element={<ProblemListPage />} />
        <Route path="problems/:slug" element={<ProblemPage />} />

        <Route path="contests" element={<ContestListPage />} />
        <Route path="contests/:slug" element={<ContestPage />} />

        <Route path="submissions" element={<SubmissionListPage />} />
        <Route path="submissions/:id" element={<SubmissionDetailPage />} />

        <Route path="ranking" element={<RankingPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="users/:username" element={<ProfilePage />} />

        <Route
          path="settings"
          element={
            <RequireAuth>
              <SettingsPage />
            </RequireAuth>
          }
        />

        <Route path="login" element={<LoginPage />} />
        <Route path="signup" element={<SignupPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />

        <Route
          path="admin"
          element={
            <RequireStaff>
              <AdminDashboardPage />
            </RequireStaff>
          }
        />
        <Route
          path="admin/problems"
          element={
            <RequireStaff>
              <AdminProblemListPage />
            </RequireStaff>
          }
        />
        <Route
          path="admin/problems/generate"
          element={
            <RequireStaff>
              <AdminGenerationPage />
            </RequireStaff>
          }
        />
        <Route
          path="admin/problems/new"
          element={
            <RequireStaff>
              <AdminProblemEditPage />
            </RequireStaff>
          }
        />
        <Route
          path="admin/problems/:slug"
          element={
            <RequireStaff>
              <AdminProblemEditPage />
            </RequireStaff>
          }
        />
        <Route
          path="admin/problems/:slug/testcases"
          element={
            <RequireStaff>
              <AdminTestcasePage />
            </RequireStaff>
          }
        />
        <Route
          path="admin/contests"
          element={
            <RequireStaff>
              <AdminContestListPage />
            </RequireStaff>
          }
        />
        <Route
          path="admin/contests/new"
          element={
            <RequireStaff>
              <AdminContestEditPage />
            </RequireStaff>
          }
        />
        <Route
          path="admin/contests/:slug"
          element={
            <RequireStaff>
              <AdminContestEditPage />
            </RequireStaff>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
      </Routes>
    </Suspense>
  );
}
